<script>
import { Place } from "../models/Place";
import { Phone } from "../models/Phone";
import { validateRequiredRefs } from "../helpers";
import { UserTypes } from "../constants/user-enums";
import { ContactTypes } from "../constants/contact-enums";

function contactSort(a, b) {
  let aPrimary = a.last_name;
  let aSecondary = "";
  if (a.type === ContactTypes.PARTNER || a.type === ContactTypes.VENDOR) {
    if (a.company_name) {
      aPrimary = a.company_name;
      aSecondary = a.last_name || "";
    } else {
      aPrimary = a.last_name;
      aSecondary = "";
    }
  }

  let bPrimary = b.last_name;
  let bSecondary = "";
  if (b.type === ContactTypes.PARTNER || b.type === ContactTypes.VENDOR) {
    if (b.company_name) {
      bPrimary = b.company_name;
      bSecondary = b.last_name || "";
    } else {
      bPrimary = b.last_name;
      bSecondary = "";
    }
  }

  return (
    aPrimary.localeCompare(bPrimary) || aSecondary.localeCompare(bSecondary)
  );
}

export default {
  name: "ProjectContractForms",
  props: {
    id: Number,
    name: String,
    type: String,
    primary_contact_id: Number,
    additional_contacts: { type: Array, default: () => [] },
    additional_contact_ids: { type: Array, default: () => [] },
    place: { type: Place, default: () => new Place() },
    email: String,
    primary_phone: {
      type: Phone,
      default: () => new Phone()
    },
    additional_phones: { type: Array, default: () => [] },
    gate_code: String,
    note: {
      type: Object,
      default: () => {
        return { text: null };
      }
    }
  },
  data() {
    return {
      autoPopulatedPrimaryPhone: null,
      contactSearchText: null,
      formPlace: this.place.clone()
    };
  },
  computed: {
    userType() {
      return this.$store.state.userType;
    },
    isGettingContacts() {
      return this.$store.state.Contacts.isGettingContacts;
    },
    contacts() {
      const contacts = this.$store.state.Contacts.contacts;
      if (contacts) {
        return contacts.map(c => {
          return {
            ...c,
            displayName: c.displayName,
            label: c.name
          };
        });
      }
      return contacts;
    },
    computedContacts() {
      if (this.contacts) {
        const contacts = this.contacts.slice();
        return contacts.sort(contactSort).reduce((acc, contact) => {
          acc.push({
            label: contact.displayName,
            subLabel: contact.primary_place.fullAddress,
            value: `${contact.id}_${contact.primary_place.id}`
          });

          contact.additional_places.forEach(place => {
            acc.push({
              label: contact.displayName,
              subLabel: place.fullAddress,
              value: `${contact.id}_${place.id}`
            });
          });

          return acc;
        }, []);
      }
      return this.contacts;
    },
    availableAdditionalContacts() {
      if (this.contacts) {
        return this.contacts
          .filter(c => {
            return (
              c.id !== this.formData.primary_contact_id &&
              this.formData.additional_contacts
                .map(ac => ac.id)
                .indexOf(c.id) === -1
            );
          })
          .sort(contactSort)
          .map(c => {
            c.label = c.displayName || "";
            c.subLabel = c.primary_place.address_1;
            c.value = Object.assign({}, c);
            return c;
          });
      }
      return [];
    },
    primaryContactAndPlaceIds: {
      get() {
        return `${this.formData.primary_contact_id}_${this.formData.place_id}`;
      },
      set(val) {
        if (val) {
          const strings = val.split("_");
          const primaryContactId = parseInt(strings[0], 10);
          const placeId = parseInt(strings[1], 10);
          this.formData.primary_contact_id = primaryContactId
            ? primaryContactId
            : undefined;
          this.formData.place_id = placeId ? placeId : undefined;
        } else {
          this.formData.primary_contact_id = undefined;
          this.formData.place_id = undefined;
        }
      }
    },
    primaryContact() {
      if (this.formData.primary_contact_id && this.contacts) {
        return this.contacts.find(
          c => c.id === this.formData.primary_contact_id
        );
      }
      return null;
    },
    primaryContactPlace() {
      if (this.formData.place_id && this.primaryContact) {
        if (this.primaryContact.primary_place.id === this.formData.place_id) {
          return this.primaryContact.primary_place;
        }
        return this.primaryContact.additional_places.find(
          p => p.id === this.formData.place_id
        );
      }
      return null;
    },
    primaryContactNameHasChanged() {
      if (this.primaryContact && this.contactSearchText) {
        return this.primaryContact.displayName !== this.contactSearchText;
      }
      return false;
    },
    placeHasChanged() {
      if (this.primaryContactPlace && this.formPlace) {
        return (
          this.primaryContactPlace.address_1 !== this.formPlace.address_1 ||
          this.primaryContactPlace.address_2 !== this.formPlace.address_2 ||
          this.primaryContactPlace.city !== this.formPlace.city ||
          this.primaryContactPlace.state !== this.formPlace.state ||
          this.primaryContactPlace.zip !== this.formPlace.zip
        );
      }
      return false;
    },
    primaryPhoneHasChanged() {
      if (this.autoPopulatedPrimaryPhone && this.formData.primary_phone) {
        return (
          this.autoPopulatedPrimaryPhone.number !==
            this.formData.primary_phone.number ||
          this.autoPopulatedPrimaryPhone.type !==
            this.formData.primary_phone.type
        );
      }
      return false;
    },
    requiredRefs() {
      if (this.userType !== UserTypes.ADMIN) {
        return [];
      }

      const refs = ["name", "type", "primaryContact", "place"];

      if (
        this.formData.primary_phone &&
        (this.formData.primary_phone.number || this.formData.primary_phone.type)
      ) {
        refs.push("primaryPhone");
      }

      if (
        this.formData.additional_phones &&
        this.formData.additional_phones.length
      ) {
        this.formData.additional_phones.forEach((p, i) => {
          if (p && (p.number || p.type)) {
            refs.push(`additionalPhone${i}`);
          }
        });
      }

      return refs;
    }
  },
  watch: {
    primaryContact(newVal) {
      if (newVal) {
        if (!this.formData.email) {
          this.formData.email = newVal.email;
        }
        if (
          !this.formData.primary_phone ||
          !this.formData.primary_phone.number
        ) {
          this.autoPopulatedPrimaryPhone = newVal.primary_phone;
          this.formData.primary_phone = this.autoPopulatedPrimaryPhone;
        }
      }
    },
    primaryContactPlace(newVal, oldVal) {
      if (newVal && newVal !== oldVal) {
        this.formPlace = newVal.clone();
      }
    }
  },
  created() {
    this.$store.dispatch("getContacts");
  },
  methods: {
    freeResponseSearch(search) {
      return this.availableAdditionalContacts.filter(contact => {
        return (
          contact.displayName.toLowerCase().indexOf(search.toLowerCase()) > -1
        );
      });
    },
    newPhoneNumber() {
      return new Phone();
    },
    removePhoneNumber(index) {
      this.formData.additional_phones.splice(index, 1);
    },
    validate() {
      return validateRequiredRefs(this, true);
    },
    preSubmit() {
      const formData = Object.assign({}, this.formData);
      if (formData.additional_contacts) {
        formData.additional_contact_ids = formData.additional_contacts.map(
          c => c.id
        );
      }

      if (this.primaryContactNameHasChanged) {
        formData.primary_contact_id = null;
      }

      if (!formData.primary_contact_id) {
        formData.new_primary_contact_name = this.contactSearchText;
      }

      if (this.placeHasChanged && formData.place_id) {
        formData.place_id = null;
        this.formPlace.id = null;
        formData.place = this.formPlace;
      }

      if (this.primaryPhoneHasChanged && formData.primary_phone.id) {
        delete formData.primary_phone.id;
      }

      return formData;
    },
    saveProject() {
      if (!this.validate()) {
        return;
      }
      const formData = this.preSubmit();

      if (this.id) {
        this.$store.dispatch("updateProject", {
          ...formData,
          isShowView: this.$route.name === "projects-show"
        });
      } else {
        this.$store.dispatch("storeProject", formData).then(res => {
          this.$store.dispatch("handleApiResponse", {
            responseStatus: res.status,
            successText: "Project stored successfully.",
            errorText: "An error occurred while attempting to store Project.",
            afterSuccessFunction: () => {
              this.$store.commit("resetCreateEditComponentParams");
              this.$router.push({
                name: "projects-show",
                params: { id: res.project.id }
              });
            }
          });
        });
      }
    },
    saveContract() {
      if (!this.validate()) {
        return;
      }
      const formData = this.preSubmit();
      formData.billing_place = this.formBillingPlace;

      if (this.id) {
        this.$store.dispatch("updateContract", {
          ...formData,
          isShowView:
            this.$route.name === "contracts-show" ||
            this.$route.name.indexOf("contract-") > -1
        });
      } else {
        this.$store.dispatch("storeContract", formData).then(res => {
          this.$store.dispatch("handleApiResponse", {
            responseStatus: res.status,
            successText: "Contract stored successfully.",
            errorText: "An error occurred while attempting to store Contract.",
            afterSuccessFunction: () => {
              this.$store.commit("resetCreateEditComponentParams");
              this.$router.push({
                name: "contracts-show",
                params: { id: res.contract.id }
              });
            }
          });
        });
      }
    }
  }
};
</script>
